import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Layout & Global Components
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import ScrollToTopButton from './components/ScrollToTopButton';
import Preloader from './components/Preloader';

// Main Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import MartynsLaw from './pages/MartynsLaw';
import Contact from './pages/Contact';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail'; 
import ComingSoon from './pages/ComingSoon'; // <-- NEW: Imported Coming Soon Page
// import Careers from './pages/Careers'; <-- HIDDEN FOR NOW
import Apply from './pages/Apply';

// Specific Service Pages
import CorporateSecurity from './pages/CorporateSecurity';
import Concierge from './pages/Concierge';
import KeyHolding from './pages/KeyHolding';
import MobilePatrols from './pages/MobilePatrols'; 

// Portal Pages
import StaffLogin from './pages/StaffLogin';
import StaffPortal from './pages/StaffPortal';

// Legal & Compliance Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ModernSlaveryStatement from './pages/ModernSlaveryStatement';


// --- ROUTE HANDLER ---
const RouteHandler = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  // 1. Force the browser to NEVER restore scroll position on refresh
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // 2. useLayoutEffect fires BEFORE the browser paints the screen, eliminating the flash!
  useLayoutEffect(() => {
    // Show the preloader instantly
    setIsLoading(true);
    
    // Jump to the top of the new page behind the preloader (handles navigation)
    window.scrollTo(0, 0);

    // Hide the preloader after 1.5 seconds
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); 

    return () => clearTimeout(timer);
  }, [location.pathname]); 

  return (
    <>
      {/* Renders the Preloader over the site while isLoading is true */}
      {isLoading && <Preloader />}
      
      {/* Hides the main site content while loading to guarantee 100% no flashing */}
      <div className="flex flex-col min-h-screen" style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
        <Header />
        
        <main className="flex-grow">
          <Routes>
            {/* Core Navigation */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/martyns-law" element={<MartynsLaw />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* News Routes */}
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            
            {/* <Route path="/careers" element={<Careers />} /> <-- HIDDEN FOR NOW */}
            <Route path="/apply" element={<Apply />} />

            {/* Service Routes */}
            <Route path="/services/corporate-security" element={<CorporateSecurity />} />
            <Route path="/services/concierge" element={<Concierge />} />
            <Route path="/services/key-holding" element={<KeyHolding />} />
            <Route path="/services/mobile-patrols" element={<MobilePatrols />} />

            {/* Staff Routes */}
            <Route path="/staff-login" element={<StaffLogin />} />
            <Route path="/staff-portal" element={<StaffPortal />} />

            {/* Compliance Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/modern-slavery" element={<ModernSlaveryStatement />} />
          </Routes>
        </main>

        <Footer />
        
        <CookieBanner />
        <ScrollToTopButton />
      </div>
    </>
  );
};

function App() {
  // --- MAINTENANCE SWITCH ---
  // Change to 'false' when you are ready to reveal the full website
  const MAINTENANCE_MODE = false; 

  if (MAINTENANCE_MODE) {
    return <ComingSoon />;
  }

  return (
    <Router>
      <RouteHandler />
    </Router>
  );
}

export default App;